import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { getCollabDraftQuery } from '@confluence/fabric-providers/entry-points/getCollabDraftQuery.ts';

import {
	type MarkdownConvertedBodyQuery$data,
	type MarkdownConvertedBodyQuery$variables,
} from './__generated__/MarkdownConvertedBodyQuery.graphql.ts';
import { MarkdownConvertedBodyQuery } from './MarkdownConvertedBodyQuery.graphql.ts';

type CollabDraftCacheShape = {
	collabDraft?: {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document?: string | null;
		version?: number | null;
		metadata?: { title?: string | null; relevance?: number | null } | null;
	} | null;
};

const COLLAB_DRAFT_VARIABLES = (contentId: string) => ({
	id: contentId,
	hydrateAdf: true,
});

/**
 * Fetch the BE-converted body and mirror it into the `CollabDraftQuery`
 * cache. Returns the body string so `flipFlagToMarkdown` can
 * `replaceDocument` the PM state before the mode flip; the cache write
 * covers the rare full-remount path (e.g. route transition) that reads
 * the body as `defaultValue`.
 *
 * Variables match `useInjectDraftContent`'s `CollabDraftQuery` read
 * (`hydrateAdf: true`, no `draftShareId`) so cache keys align. Pages
 * opened via a draft-share link are not covered — markdown-mode convert
 * is owner-initiated.
 *
 * Always resolves; on failure returns `undefined` and lets NCS catchupv2
 * recover — a thrown error would break the convert flow.
 *
 * Local mirror of `@confluence/editor-features/refetchEditorContent` to
 * avoid a circular package dep (editor-features → emoji-title →
 * content-markdown-mode).
 */
export const refetchPageContent = async (contentId: string): Promise<string | undefined> => {
	let convertedBody: string | undefined;
	try {
		const monolithResult = await queryInterop<
			MarkdownConvertedBodyQuery$data,
			MarkdownConvertedBodyQuery$variables
		>({
			query: MarkdownConvertedBodyQuery,
			context: { single: true },
			fetchPolicy: 'network-only',
			variables: { contentId, status: ['draft', 'current'] },
		});

		const value = monolithResult?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.value;
		if (typeof value === 'string' && value.length > 0) {
			convertedBody = value;
		}
	} catch {
		return undefined;
	}

	if (!convertedBody) {
		return undefined;
	}

	// Best-effort cache mirror. Isolated so a cache miss / write failure does
	// not drop the body the caller needs for `replaceDocument` — that path
	// is what keeps the editor populated across the mode flip.
	const collabDraftQuery = getCollabDraftQuery();
	try {
		const existing = readQueryInterop<CollabDraftCacheShape>({
			query: collabDraftQuery,
			variables: COLLAB_DRAFT_VARIABLES(contentId),
		});

		// Preserve version + metadata (incl. `relevance` on the WithRelevance
		// variant); swap only `document`.
		writeQueryInterop<CollabDraftCacheShape>({
			query: collabDraftQuery,
			variables: COLLAB_DRAFT_VARIABLES(contentId),
			data: {
				collabDraft: {
					...existing?.collabDraft,
					document: convertedBody,
				},
			},
		});
	} catch {
		// see JSDoc
	}

	return convertedBody;
};
